import { NumberField } from '@/components/form/fields/text/NumberInput';
import DaasTableRowCell from '@/components/table/DaasTableRowCell';
import EllipsizedWithTooltip from '@/components/tooltip/EllipsizedWithTooltip';
import { useProduct } from '@/services/api/hooks/product';
import { FC, FunctionComponent, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { StockLevelProductImage } from './StockLevelProductImage';
import { StockTableData } from './stockLevel.types';

interface Props {
  data: StockTableData;
  index: number;
  editMode?: boolean;
  readonly?: boolean;
}


export const StockLevelRow: FunctionComponent<Props> = ({ data, index, editMode, readonly }) => {
  const [product] = useProduct(data.productId);

  const specs = product.propertyBag.properties?.map(spec => spec.text).join(' / ');

  const { watch, setError, clearErrors } = useFormContext();

  const maxStockLevel = watch(`stockData.${index}.maxStockLevel`);
  const minStockLevel = watch(`stockData.${index}.minStockLevel`);

  const minMaxError = maxStockLevel <= minStockLevel && !(maxStockLevel === 0 && minStockLevel === 0);

  useEffect(() => {
    if (minMaxError) {
      setError('stockData', { message: 'Max level must be larger than min level.' });
    } else {
      clearErrors('stockData');
    }
  }, [minMaxError, setError, clearErrors]);

  return (
    <>
      <DaasTableRowCell className="justify-start">
        <div className="flex flex-row space-x-5 w-full h-[100px] items-center">
          <div className="w-[150px] h-full flex items-center">
            <StockLevelProductImage product={product} />
          </div>
          <div className="flex-1">
            <p className="text-lg">{product.manufacturer.name}</p>
            <p className="text-2xl font-semibold">{product.name}</p>
            <EllipsizedWithTooltip
              // Calc is full viewport - LHP / total table fraction to get value of 1fr
              // Since device table row is 1fr
              className="max-w-[calc((100vw-25rem)/1.8)]"
              id={`${product.id}-specs`}
              elementText={specs}
              tooltipText={specs}
            />
          </div>
        </div>
      </DaasTableRowCell>

      <DaasTableRowCell>
        <div className="text-lg">{product.productId}</div>
      </DaasTableRowCell>

      <DaasTableRowCell>
        {readonly ? (
          <div className="text-2xl font-semibold">{data.minStockLevel}</div>
        ) : (
          <div>
            <NumberField id={`stockData.${index}.minStockLevel`} readonly={!editMode} min={0} className="mb-2" />

            {minMaxError && <div className="text-red-500">Min must be below max</div>}
          </div>
        )}
      </DaasTableRowCell>
      <DaasTableRowCell>
        {readonly ? (
          <div className="text-2xl font-semibold">{data.maxStockLevel}</div>
        ) : (
          <div>
            <NumberField id={`stockData.${index}.maxStockLevel`} readonly={!editMode} min={0} className="mb-2" />

            {minMaxError && <div className="text-red-500">Min must be below max</div>}
          </div>
        )}
      </DaasTableRowCell>
    </>
  );
};
